import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import type { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import { JurisdictionField } from '@src/permitTypesManager/permitTypeSections/PermitDefinitionFields';
import {
  CommodityField,
  EFFOverhangField,
  FrontOverhangField,
  LoadDescriptionField,
  LoadDispositionField,
  LoadEndToEndCount,
  LoadFloridaDescriptionField,
  LoadFloridaIdentityField,
  LoadFloridaPermitTypeField,
  LoadHeightField,
  LoadIndustryTypeField,
  LoadIsNewlyManufacturedField,
  LoadLengthField,
  LoadLoadingMethodField,
  LoadMakeField,
  LoadModelField,
  LoadOwnerAddressField,
  LoadOwnerNameField,
  LoadQuantityField,
  LoadSealedContainerChassisNumberField,
  LoadSealedContainerNumberField,
  LoadSerialNumberField,
  LoadSideBySideCount,
  LoadStackCountField,
  LoadWeightField,
  LoadWidthField,
  LoadYearField,
  OverallHeightField,
  OverallLengthField,
  OverallWeightField,
  OverallWidthField,
  RearOverhangField,
  SingleItemField,
} from '@src/permitTypesManager/permitTypeSections/PermitLoadFields';
import type { SectionField } from '@src/permitTypesManager/permitTypeSections/PermitTypeSectionCheboxList';
import { PermitTypeSectionCheckboxList } from '@src/permitTypesManager/permitTypeSections/PermitTypeSectionCheboxList';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const PermitLoadSection = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<AddPermitTypeFormInputs>();
  const [permitJurisdiction] = useWatch({
    control,
    name: [JurisdictionField.fieldName],
  });
  const loadInformationFields: SectionField[] = [
    {
      field: LoadDescriptionField,
      label: t('fields.loadDescription'),
    },
    {
      field: CommodityField,
      label: t('fields.commodity'),
    },
    {
      field: LoadMakeField,
      label: t('fields.loadMake'),
    },
    {
      field: LoadModelField,
      label: t('fields.loadModel'),
    },
    {
      field: LoadSerialNumberField,
      label: t('fields.loadSerialNumber'),
    },
    {
      field: LoadIndustryTypeField,
      label: t('fields.loadIndustryType'),
    },
    {
      field: LoadQuantityField,
      label: t('fields.loadQuantity'),
    },
    {
      field: LoadYearField,
      label: t('fields.loadYear'),
    },
    {
      field: LoadLoadingMethodField,
      label: t('fields.loadLoadingMethod'),
    },
    {
      field: LoadDispositionField,
      label: t('fields.loadDisposition'),
    },
    {
      field: LoadSideBySideCount,
      label: t('fields.loadSideBySideCount'),
    },
    {
      field: LoadEndToEndCount,
      label: t('fields.loadEndToEndCount'),
    },
    {
      field: LoadStackCountField,
      label: t('fields.loadStackCount'),
    },
    {
      field: LoadIsNewlyManufacturedField,
      label: t('fields.loadIsNewlyManufactured'),
    },
    {
      field: LoadOwnerNameField,
      label: t('fields.loadOwnerName'),
    },
    {
      field: LoadOwnerAddressField,
      label: t('fields.loadOwnerAddress'),
    },
    {
      field: LoadLengthField,
      label: t('fields.loadLength'),
    },
    {
      field: LoadWidthField,
      label: t('fields.loadWidth'),
    },
    {
      field: LoadHeightField,
      label: t('fields.loadHeight'),
    },
    {
      field: LoadWeightField,
      label: t('fields.loadWeight'),
    },
    {
      field: LoadSealedContainerNumberField,
      label: t('fields.loadSealedContainerNumber'),
    },
    {
      field: LoadSealedContainerChassisNumberField,
      label: t('fields.loadSealedContainerChassisNumber'),
    },
    {
      field: SingleItemField,
      label: t('fields.loadIsSingleItem'),
    },
  ];

  const flFields: SectionField[] = [
    { field: LoadFloridaPermitTypeField, label: t('fields.floridaPermitType') },
    {
      field: LoadFloridaDescriptionField,
      label: t('fields.floridaLoadDescription'),
    },
    {
      field: LoadFloridaIdentityField,
      label: t('fields.floridaLoadIdentity'),
    },
  ];

  const axleConfigurationFields = [
    {
      field: OverallLengthField,
      label: t('fields.overallLength'),
    },
    {
      field: OverallWidthField,
      label: t('fields.overallWidth'),
    },
    {
      field: OverallHeightField,
      label: t('fields.overallHeight'),
    },
    {
      field: OverallWeightField,
      label: t('fields.overallWeight'),
    },
    {
      field: FrontOverhangField,
      label: t('fields.overallFrontOverhang'),
    },
    {
      field: RearOverhangField,
      label: t('fields.overallRearOverhang'),
    },
    {
      field: EFFOverhangField,
      label: t('fields.overallEFFOverhang'),
    },
  ];
  return (
    <Stack direction="column" gap={theme.spacing['1']}>
      <Typography style={theme.typography.label.large}>
        {t('orderSections.loadInformation')}
      </Typography>
      <PermitTypeSectionCheckboxList
        sectionFields={loadInformationFields}
        control={control}
      />
      {permitJurisdiction == 'FL' && (
        <PermitTypeSectionCheckboxList
          sectionFields={flFields}
          control={control}
        />
      )}
      <Typography style={theme.typography.label.large}>
        {t('orderSections.axlesConfiguration')}
      </Typography>
      <PermitTypeSectionCheckboxList
        sectionFields={axleConfigurationFields}
        control={control}
      />
    </Stack>
  );
};
